import { userEntryToNumber } from '@ebiz/pretty-num';
import { assert } from '@ember/debug';
import type { Column } from 'slickgrid';
import Validation from './validation.ts';
import type { ColumnFilter } from '../components/aditools-device-table.ts';
import type { DeviceTableDeviceData } from '@ebiz/aditools-databook';

const _stripTrailingDecimal = function (item: string) {
  const dotted = item.split('.');
  return dotted.length === 2 && dotted[1] && dotted[1].length === 0 ? item.slice(0, -1) : item;
};

export default class EngineeringRangeValueFilter implements ColumnFilter<DeviceTableDeviceData> {
  defaultMinimum: string;
  defaultMaximum: string;
  minimumValue: string;
  maximumValue: string;
  qaName?: string;
  constructor(defaultMinimum?: string, defaultMaximum?: string, qaName?: string) {
    this.defaultMinimum = defaultMinimum ?? '';
    this.defaultMaximum = defaultMaximum ?? '';
    this.minimumValue = this.defaultMinimum;
    this.maximumValue = this.defaultMaximum;
    this.qaName = qaName;
  }

  populate(column: Column<DeviceTableDeviceData>): HTMLElement {
    if (!column) {
      throw 'column passed to populate is invalid';
    }
    const selection = this.generateDOMElement(column);
    const minValueElement = selection.children[0] as HTMLInputElement;
    const maxValueElement = selection.children[2] as HTMLInputElement;
    minValueElement.value = this.minimumValue;
    maxValueElement.value = this.maximumValue;
    assert('At least one element is always generated', selection);
    return selection;
  }

  generateDOMElement(column: Column<DeviceTableDeviceData>): HTMLDivElement {
    const inputQaElement = this.qaName ? `data-qa-element="device_table_${this.qaName}_input"` : '';
    const divElement = document.createElement('div');
    divElement.className = 'slickgrid-filter-engineering';
    divElement.innerHTML = `<input id="filter-${column.id}-val-min" class="slickgrid-filter-range-value" type="text" autocomplete="off" aria-label="${column.name} minimum value" ${inputQaElement} data-column-id="${column.id}" />
      <span class='slickgrid-filter-range-hyphen'>&#x2012;</span>
      <input id="filter-${column.id}-val-max" class="slickgrid-filter-range-value" type="text" autocomplete="off" aria-label="${column.name} maximium value" ${inputQaElement} data-column-id="${column.id}" />`;
    return divElement;
  }

  update(element: HTMLInputElement): void {
    const children = element.parentElement?.children;
    if (children) {
      this.minimumValue = (<HTMLInputElement>children[0]).value;
      this.maximumValue = (<HTMLInputElement>children[2]).value;
    }
  }

  unpack(packedValues: string): void {
    if (packedValues) {
      const initialValues = JSON.parse(packedValues);
      if (
        Object.prototype.hasOwnProperty.call(initialValues, 'minimumValue') &&
        Object.prototype.hasOwnProperty.call(initialValues, 'maximumValue')
      ) {
        this.minimumValue = initialValues.minimumValue;
        this.maximumValue = initialValues.maximumValue;
      }
    }
  }

  reset(): void {
    this.minimumValue = this.defaultMinimum;
    this.maximumValue = this.defaultMaximum;
  }

  hasValue(): boolean {
    return this.minimumValue !== '' || this.maximumValue !== '';
  }

  pack(): string {
    return JSON.stringify({ minimumValue: this.minimumValue, maximumValue: this.maximumValue });
  }

  filter(field: string): boolean {
    // Temporary workaround for Package Area
    const cleanedField = field ? (field.includes('mm') ? (field.split('mm')[0] ?? field) : field) : field;
    let fieldValue = cleanedField === '' || cleanedField === '-' ? 0 : userEntryToNumber(cleanedField, Infinity);
    fieldValue = isNaN(fieldValue) ? Infinity : fieldValue;
    const filterMinimumString = _stripTrailingDecimal(this.minimumValue);
    const filterMaximumString = _stripTrailingDecimal(this.maximumValue);
    if (
      Validation.validateFieldInput(filterMinimumString, 'ENGINEERING') ||
      Validation.validateFieldInput(filterMaximumString, 'ENGINEERING')
    ) {
      let filterMinValue = userEntryToNumber(filterMinimumString, Infinity);
      filterMinValue = isNaN(filterMinValue) ? Infinity : filterMinValue;
      let filterMaxValue = userEntryToNumber(filterMaximumString, Infinity);
      filterMaxValue = isNaN(filterMaxValue) ? Infinity : filterMaxValue;
      if (fieldValue || fieldValue === 0) {
        if (filterMinValue !== Infinity && filterMaxValue !== Infinity) {
          return fieldValue >= filterMinValue && fieldValue <= filterMaxValue;
        } else if (filterMinValue === Infinity && filterMaxValue !== Infinity) {
          return fieldValue <= filterMaxValue;
        } else if (filterMinValue !== Infinity && filterMaxValue === Infinity) {
          return fieldValue >= filterMinValue;
        }
      }
    }
    return true;
  }
}
